import { Link } from "react-router-dom";
import { Stethoscope, ActivitySquare, Clock3, ArrowRight } from "lucide-react";
import { SEO } from "@/components/SEO";
import { Reveal } from "@/components/Reveal";
import { PageHero, SectionHeading, FinalCTA } from "@/components/shared";
import { symptoms, routes } from "@/lib/site";
import { combine, localBusinessSchema, breadcrumbSchema } from "@/lib/schema";

export default function Symptoms() {
  const jsonLd = combine(
    localBusinessSchema,
    breadcrumbSchema([
      { name: "Home", path: routes.home },
      { name: "Symptoms", path: routes.symptoms },
    ])
  );

  return (
    <>
      <SEO
        path={routes.symptoms}
        title="Common Pain & Mobility Symptoms | Chiropractor in Varanasi"
        description="Understand common pain and mobility symptoms — back pain, neck pain, sciatica, stiffness and more. Learn when to seek chiropractic consultation in Varanasi with Dr. Satyam Jaiswal."
        jsonLd={jsonLd}
      />
      <PageHero
        eyebrow="Symptoms"
        title="Common Pain and Mobility Symptoms"
        sub="Understanding your symptoms is the first step. Here are common concerns we help with — and when a professional consultation may be helpful."
        breadcrumb={[{ label: "Symptoms" }]}
      />

      <section className="bg-white py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {symptoms.map((s, i) => (
              <Reveal key={s.name} delay={(i % 2) * 80}>
                <div
                  data-testid={`symptom-${s.name.toLowerCase().replace(/\s/g, "-")}`}
                  className="h-full bg-soft rounded-2xl p-8 hover:shadow-md transition-shadow duration-300 border border-transparent hover:border-brand-orange/20"
                >
                  <h3 className="font-heading font-bold text-navy text-xl">{s.name}</h3>
                  <p className="mt-3 text-ink leading-relaxed">{s.desc}</p>
                  <div className="mt-5 space-y-3">
                    <div className="flex gap-3">
                      <ActivitySquare className="h-5 w-5 text-brand-orange shrink-0 mt-0.5" />
                      <p className="text-sm text-ink"><span className="font-semibold text-navy">Impact: </span>{s.impact}</p>
                    </div>
                    <div className="flex gap-3">
                      <Clock3 className="h-5 w-5 text-brand-orange shrink-0 mt-0.5" />
                      <p className="text-sm text-ink"><span className="font-semibold text-navy">When to consult: </span>{s.when}</p>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal>
            <div className="mt-14 rounded-2xl bg-navy/5 p-8 flex flex-col sm:flex-row items-start sm:items-center gap-5">
              <div className="grid h-12 w-12 place-items-center rounded-xl bg-navy text-white shrink-0">
                <Stethoscope className="h-6 w-6" />
              </div>
              <p className="text-ink leading-relaxed">
                This information is for general awareness only and is not a diagnosis. If your symptoms
                persist, worsen or affect your daily life, a professional consultation can help you
                understand your concern and appropriate care options.
              </p>
            </div>
          </Reveal>

          <div className="mt-10 flex flex-wrap gap-4">
            <Link to={routes.treatments} className="inline-flex items-center gap-2 font-bold text-navy hover:text-brand-orange transition-colors group">
              View Treatments <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to={routes.contact} className="inline-flex items-center gap-2 font-bold text-navy hover:text-brand-orange transition-colors group">
              Contact the Clinic <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      <FinalCTA title="Not Sure About Your Symptoms?" sub="Book a consultation with Dr. Satyam Jaiswal for a careful assessment and personalized guidance." />
    </>
  );
}
